import * as React from "react";
import { de, enUS, es, fr, it } from "date-fns/locale";
import { CalendarIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

/*
 * AOS design-system date picker — permanent convention: NO native date inputs.
 * String contract identical to <input type="date">: value/min are "YYYY-MM-DD".
 * Localized month names (date-fns locales) + Intl display formatting.
 */

const DF_LOCALES = { en: enUS, es, de, fr, it };

const toDate = (s) => (s ? new Date(`${s}T00:00:00`) : undefined);
const toStr = (d) =>
  d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}` : "";

export const DatePicker = ({
  value,
  onChange,
  min,
  id,
  className,
  placeholder,
  disabled,
  "data-testid": testid,
  "aria-label": ariaLabel,
}) => {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const lang = (i18n.resolvedLanguage || "en").slice(0, 2);
  const date = toDate(value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          id={id}
          data-testid={testid}
          disabled={disabled}
          aria-label={ariaLabel || t("common.selectDate")}
          className={cn(
            "flex items-center gap-2.5 w-full px-3 text-left text-sm font-normal border transition-colors duration-200",
            "hover:border-black/25 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0A0A0A]/20 disabled:opacity-50",
            !date && "text-[#737373]",
            className
          )}
        >
          <CalendarIcon className="w-4 h-4 text-black/30 shrink-0" aria-hidden="true" />
          <span className="truncate">
            {date
              ? new Intl.DateTimeFormat(i18n.resolvedLanguage, { dateStyle: "medium" }).format(date)
              : placeholder || t("common.selectDate")}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 rounded-2xl shadow-xl border-black/5" align="start">
        <Calendar
          mode="single"
          selected={date}
          defaultMonth={date || (min ? toDate(min) : undefined)}
          locale={DF_LOCALES[lang] || enUS}
          disabled={min ? { before: toDate(min) } : undefined}
          onSelect={(d) => {
            if (d) onChange(toStr(d));
            setOpen(false);
          }}
          classNames={{
            cell: "h-10 w-10 text-center text-sm p-0 relative focus-within:relative focus-within:z-20",
            day: "h-10 w-10 p-0 font-normal rounded-full hover:bg-black/5 transition-colors duration-150 aria-selected:opacity-100",
            head_cell: "text-[#737373] rounded-md w-10 font-normal text-[0.8rem]",
          }}
        />
      </PopoverContent>
    </Popover>
  );
};
